/**
 * Cart Location Gate (AU)
 * ------------------------------------------------------------------
 * Before viewing the cart drawer / popup or cart page, the shopper picks
 * a state + postcode. The location is stored and combined with the cart's
 * shipping "facts" (rendered by snippets/cart-shipping-facts.liquid) and
 * run through window.ADSShipping.resolve() to decide:
 *   - the free-shipping progress-bar threshold,
 *   - "不包邮" items excluded from the progress bar (real-cost freight),
 *   - "不配送" gating (grey out checkout + banner), lifted while the
 *     CS ShipZip widget's "Store Pickup" tab is active.
 *
 * On checkout the state + postcode are appended to the checkout URL as
 * Shopify prefill params so the address is pre-filled (where the real
 * postcode rate calculation runs).
 *
 * All rules live in assets/shipping-rules.js — this file is pure UI glue.
 */
(function () {
  "use strict";

  var STORAGE_KEY = "clg_au_location";

  // Amount (in cents) currently excluded from the free-shipping progress
  // bar because the items are "不包邮" (Heavy / cat-litter / ≥5kg AUSPOST).
  var NO_FREE_CENTS = 0;

  var STATE_NAMES = {
    NSW: "New South Wales",
    ACT: "Australian Capital Territory",
    VIC: "Victoria",
    QLD: "Queensland",
    SA: "South Australia",
    WA: "Western Australia",
    TAS: "Tasmania",
    NT: "Northern Territory",
  };

  /* -------------------------------------------------------------- */
  /*  Location storage (state + postcode only)                       */
  /* -------------------------------------------------------------- */

  function getStored() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      var data = JSON.parse(raw);
      if (!data || !data.state || !data.zip) return null;
      // Re-validate the postcode still belongs to the state.
      if (!window.ADSShipping || ADSShipping.detectState(data.zip) !== data.state) {
        return null;
      }
      return { state: data.state, zip: data.zip, stateName: STATE_NAMES[data.state] || data.state };
    } catch (e) {
      return null;
    }
  }

  function setStored(state, zip) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ state: state, zip: zip }));
    } catch (e) {}
  }

  function clearStored() {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (e) {}
  }

  /* -------------------------------------------------------------- */
  /*  Cart facts + resolve                                           */
  /* -------------------------------------------------------------- */

  function readFacts() {
    var el = document.getElementById("ads-cart-facts");
    if (!el) return {};
    try {
      return JSON.parse(el.textContent || "{}");
    } catch (e) {
      return {};
    }
  }

  /** Resolve shipping for the current location + cart, or null if no location. */
  function resolveCurrent() {
    var loc = getStored();
    if (!loc || !window.ADSShipping) return null;
    var result = ADSShipping.resolve(loc.state, loc.zip, readFacts());
    result.loc = loc;
    return result;
  }

  /* -------------------------------------------------------------- */
  /*  Helpers                                                        */
  /* -------------------------------------------------------------- */

  function formatMoney(dollars) {
    var cents = Math.round(dollars * 100);
    if (window.Shopify && Shopify.formatMoney && window.cartStrings && cartStrings.money_format) {
      try {
        return Shopify.formatMoney(cents, cartStrings.money_format);
      } catch (e) {}
    }
    return "$" + dollars.toFixed(2);
  }

  function isCartEmpty() {
    var el = document.querySelector(".cart-count:not(.cart-count-drawer)");
    if (el) {
      var n = parseInt((el.textContent || "").replace(/[^\d]/g, ""), 10);
      if (!isNaN(n)) return n <= 0;
    }
    if (document.querySelector(".minicart-body .items")) return false;
    if (document.querySelector("cart-items.is-empty")) return true;
    return false;
  }

  function isCartPage() {
    return !!document.getElementById("main-cart-items");
  }

  /**
   * True when the CS ShipZip widget's "Store Pickup" tab (cs-tab3) is the
   * active one. Pickup resolves every delivery restriction, so the gate must
   * not block checkout in that mode. Desktop uses the tab list, mobile the
   * accordion headings; the app re-renders both asynchronously.
   */
  function isPickupMode() {
    // The widget is only offered to VIC shoppers (see cart-checkout-gate.liquid).
    var loc = getStored();
    if (!loc || loc.state !== "VIC") return false;
    var root = document.getElementById("csShippingAppCode");
    if (!root || root.closest("[hidden]")) return false;
    var tab = root.querySelector('.cs-crawler-tabs li.active[rel="cs-tab3"]');
    if (tab) return true;
    var heading = root.querySelector('.tab_drawer_heading.d_active[rel="cs-tab3"]');
    return !!heading;
  }

  /* -------------------------------------------------------------- */
  /*  Progress-bar re-basing + "不包邮" exclusion                     */
  /* -------------------------------------------------------------- */

  /**
   * Patch a <free-ship-progress-bar> so every init() call (including the
   * theme's own on cart update) subtracts the excluded "不包邮" amount from
   * the order subtotal before computing the percentage.
   */
  function patchBar(bar) {
    if (bar._adsPatched) return;
    var orig = bar.init;
    if (typeof orig !== "function") return;
    bar.init = function (order) {
      var cents = Number(order);
      if (isNaN(cents)) cents = Number(bar.dataset.order) || 0;
      var reduced = cents - (NO_FREE_CENTS || 0);
      if (reduced < 0) reduced = 0;
      return orig.call(bar, reduced);
    };
    bar._adsPatched = true;
  }

  function applyState() {
    var result = resolveCurrent();
    if (result) {
      result.pickupMode = isPickupMode();
      if (result.pickupMode && result.blocked) {
        // Shopper chose store pickup in the ShipZip widget: nothing is
        // "delivered", so the blocked items are fine. Clear the block but
        // keep the threshold/fee info untouched for the chip/bar.
        result.blocked = false;
        result.blockedItems = [];
      }
    }
    NO_FREE_CENTS = result && result.noFree ? Number(result.noFreeAmount) || 0 : 0;

    var bars = document.querySelectorAll("free-ship-progress-bar");
    bars.forEach(function (bar) {
      patchBar(bar);
      if (result && result.threshold) {
        bar.style.display = "";
        var target = String(result.threshold);
        // Re-init when threshold or exclusion changed (avoid observer loops).
        var stamp = target + ":" + NO_FREE_CENTS;
        if (bar.dataset.feAmount !== target || bar.dataset.clgApplied !== stamp) {
          bar.dataset.feAmount = target;
          bar.dataset.clgApplied = stamp;
          if (typeof bar.init === "function") bar.init(bar.dataset.order);
        }
      } else if (result) {
        // No simple free-shipping threshold (weight / real-cost / blocked).
        bar.style.display = "none";
      }
      ensureChip(bar, result);
    });

    renderBanner(result);
    setCheckoutBlocked(result && result.blocked);

    // Broadcast so other widgets (e.g. the VIC-only CS ShipZip widget) can
    // react to state/postcode changes.

    try {
      document.dispatchEvent(
        new CustomEvent("clg:applied", {
          detail: { result: result, loc: (result && result.loc) || getStored() },
        })
      );
    } catch (e) {}
  }

  /* -------------------------------------------------------------- */
  /*  Chip above the progress bar                                    */
  /* -------------------------------------------------------------- */

  function shipSummary(result) {
    if (!result) return "";
    if (result.pickupMode) return "Store pickup — Moorabbin VIC 3189.";
    if (result.blocked) return "Not available for delivery to this address.";
    if (result.mode === "threshold" && result.threshold) {
      return "Free over " + formatMoney(result.threshold) +
        (result.fee ? " · under " + formatMoney(result.threshold) + " delivery " + formatMoney(result.fee) : "");
    }
    if (result.mode === "weight") {
      if (!result.fee) return result.message || "Shipping is calculated by parcel weight at checkout.";
      return "Shipping " + formatMoney(result.fee) + " (by parcel weight)";
    }
    if (result.mode === "auspost") {
      if (result.threshold) {
        return "AUSPOST – Free Shipping on Orders $69+ (up to 5kg). Orders under $69 $12 Shipping.";
      }
      return result.message || "Shipping calculated at checkout.";
    }
    return "";
  }

  function ensureChip(bar, result) {
    if (!bar || !bar.parentNode || !result || !result.loc) return;
    var chip = bar.previousElementSibling;
    if (!chip || !chip.classList || !chip.classList.contains("clg-chip")) {
      chip = document.createElement("div");
      chip.className = "clg-chip";
      chip.innerHTML =
        '<div class="clg-chip__row">' +
        '<span class="clg-chip__loc"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0Z"/><circle cx="12" cy="10" r="3"/></svg> <b data-clg-loc></b></span>' +
        '<button type="button" class="clg-chip__edit" data-clg-edit>' +
        ((CartLocationGate.el && CartLocationGate.el.dataset.editLabel) || "Change") +
        "</button>" +
        "</div>" +
        '<div class="clg-chip__ship" data-clg-ship></div>';
      bar.parentNode.insertBefore(chip, bar);
    }
    // Chip may be hidden when the bar is hidden — keep it visible either way.
    chip.style.display = "";
    var loc = chip.querySelector("[data-clg-loc]");
    var ship = chip.querySelector("[data-clg-ship]");
    if (loc) loc.textContent = result.loc.stateName + " " + result.loc.zip;
    if (ship) ship.textContent = shipSummary(result);
  }

  /* -------------------------------------------------------------- */
  /*  Banner near the checkout button                                */
  /* -------------------------------------------------------------- */

  function itemsList(items) {
    if (!items || !items.length) return "";
    var lis = items
      .map(function (t) { return "<li>" + escapeHtml(t) + "</li>"; })
      .join("");
    return '<ul class="ads-ship-banner__list">' + lis + "</ul>";
  }

  function escapeHtml(s) {
    return String(s == null ? "" : s)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");
  }

  function renderBanner(result) {
    var banners = document.querySelectorAll("[data-ads-banner]");
    banners.forEach(function (b) {
      b.className = "ads-ship-banner";
      if (!result || (!result.blocked && !result.noFree)) {
        b.hidden = true;
        b.innerHTML = "";
        return;
      }
      if (result.blocked) {
        b.classList.add("ads-ship-banner--block");
        b.innerHTML =
          "<strong>The following item(s) can't be delivered to this address. Please remove them" +
          (result.pickupGuide ? ", or choose store pickup," : "") +
          " to continue.</strong>" +
          itemsList(result.blockedItems);
      } else {
        b.classList.add("ads-ship-banner--nofree");
        b.innerHTML =
          "<strong>The following item(s) don't count toward free shipping — final freight is charged at cost.</strong>" +
          itemsList(result.noFreeItems);
      }
      b.hidden = false;
    });
  }

  /* -------------------------------------------------------------- */
  /*  Checkout blocking                                              */
  /* -------------------------------------------------------------- */

  function checkoutButtons() {
    // The cart-page delivery gate (#cdg-shipping-checkout) manages its own
    // disabled state; the Click & Collect button is the escape hatch.
    return document.querySelectorAll(
      '.btn-checkout, button[name="checkout"], #cart-checkout-btn'
    );
  }

  function setCheckoutBlocked(blocked) {
    checkoutButtons().forEach(function (btn) {
      if (blocked) {
        if (btn.dataset.adsBlocked !== "1") {
          // Remember the theme's own disabled state (e.g. T&C checkbox).
          btn.dataset.adsPrevDisabled = btn.disabled ? "1" : "0";
          btn.dataset.adsBlocked = "1";
        }
        btn.disabled = true;
      } else if (btn.dataset.adsBlocked === "1") {
        btn.disabled = btn.dataset.adsPrevDisabled === "1";
        delete btn.dataset.adsBlocked;
        delete btn.dataset.adsPrevDisabled;
      }
    });
  }

  /* -------------------------------------------------------------- */
  /*  The gate element                                               */
  /* -------------------------------------------------------------- */

  var CartLocationGate = {
    el: null,
    form: null,
    stateSelect: null,
    zipInput: null,
    errorEl: null,
    context: "drawer",
    suppressAutoOpen: false,

    init: function () {
      this.el = document.getElementById("cart-location-gate");
      if (!this.el) return;

      this.form = this.el.querySelector("[data-clg-form]");
      this.stateSelect = this.el.querySelector("[data-clg-state]");
      this.zipInput = this.el.querySelector("[data-clg-zip]");
      this.errorEl = this.el.querySelector("[data-clg-error]");

      this.bindForm();
      this.bindClose();
      this.observeDrawer();
      this.observeCartMutations();
      this.bindCheckout();
      this.bindChipEdit();
      this.observePickupTab();

      var stored = getStored();
      if (stored) this.prefill(stored);

      if (isCartPage()) {
        if (!stored && !isCartEmpty()) {
          this.open("cart");
        } else {
          applyState();
        }
      } else {
        applyState();
      }
    },

    prefill: function (data) {
      if (this.stateSelect) this.stateSelect.value = data.state;
      if (this.zipInput) this.zipInput.value = data.zip;
    },

    bindForm: function () {
      var self = this;
      if (!this.form) return;
      this.form.addEventListener("submit", function (e) {
        e.preventDefault();
        self.submit();
      });
      [this.stateSelect, this.zipInput].forEach(function (input) {
        if (!input) return;
        input.addEventListener("input", function () { self.showError(""); });
        input.addEventListener("change", function () { self.showError(""); });
      });
    },

    submit: function () {
      var state = this.stateSelect ? this.stateSelect.value : "";
      var zip = this.zipInput ? this.zipInput.value.trim() : "";

      if (!state) { this.showError(this.msg("state")); return; }
      if (!zip) { this.showError(this.msg("zip")); return; }
      if (!window.ADSShipping || !/^\d{3,4}$/.test(zip) || ADSShipping.detectState(zip) !== state) {
        this.showError(this.msg("mismatch"));
        return;
      }

      setStored(state, zip);
      this.showError("");
      applyState();
      // Keep the cart-page checkout gate's postcode field in sync.
      if (window.CartDeliveryGate && typeof CartDeliveryGate.refresh === "function") {
        CartDeliveryGate.refresh();
      }
      
      this.close(true);
    },

    showError: function (text) {
      if (!this.errorEl) return;
      this.errorEl.textContent = text || "";
      this.errorEl.style.display = text ? "block" : "none";
    },

    msg: function (key) {
      var el = this.el;
      var map = {
        state: (el && el.dataset.msgState) || "Please choose your state.",
        zip: (el && el.dataset.msgZip) || "Please enter your postcode.",
        mismatch:
          (el && el.dataset.msgMismatch) ||
          "That postcode doesn't match the selected state. Please check and try again.",
      };
      return map[key];
    },

    open: function (reason) {
      this.context = reason || "drawer";
      if (!this.el) return;
      this.el.classList.add("clg-open");
      this.el.setAttribute("aria-hidden", "false");
      document.body.classList.add("clg-active");
      if (this.context === "cart") document.body.classList.add("clg-cart-locked");
      var stored = getStored();
      if (stored) this.prefill(stored);
      var self = this;
      window.requestAnimationFrame(function () {
        if (self.zipInput && self.stateSelect && self.stateSelect.value) {
          self.zipInput.focus();
        } else if (self.stateSelect) {
          self.stateSelect.focus();
        }
      });
    },

    close: function (saved) {
      if (!this.el) return;
      this.el.classList.remove("clg-open");
      this.el.setAttribute("aria-hidden", "true");
      document.body.classList.remove("clg-active");
      document.body.classList.remove("clg-cart-locked");

      var reopen = document.querySelector(".clg-reopen");
      if (reopen) reopen.remove();

      if (!saved && this.context === "drawer") {
        this.suppressAutoOpen = true;
        var self = this;
        setTimeout(function () { self.suppressAutoOpen = false; }, 900);
        var closeBtn = document.querySelector(".minicart__wrapper .close-cart-button");
        if (closeBtn) closeBtn.click();
      }
    },

    showReopen: function () {
      if (document.querySelector(".clg-reopen")) return;
      var self = this;
      var btn = document.createElement("button");
      btn.type = "button";
      btn.className = "clg-reopen btn-primary";
      btn.textContent = (this.el && this.el.dataset.reopenLabel) || "Set delivery location";
      btn.addEventListener("click", function () {
        btn.remove();
        self.open("cart");
      });
      document.body.appendChild(btn);
    },

    bindClose: function () {
      var self = this;
      this.el.querySelectorAll("[data-clg-close]").forEach(function (btn) {
        btn.addEventListener("click", function (e) {
          e.preventDefault();
          self.close(false);
        });
      });
      var backdrop = this.el.querySelector("[data-clg-backdrop]");
      if (backdrop) {
        backdrop.addEventListener("click", function () {
          if (self.context === "drawer") self.close(false);
        });
      }
    },

    observeDrawer: function () {
      var wrapper = document.querySelector(".minicart__wrapper");
      if (!wrapper) return;
      var self = this;
      var obs = new MutationObserver(function () {
        if (wrapper.classList.contains("open")) {
          if (self.suppressAutoOpen) return;
          if (!getStored() && !isCartEmpty()) {
            self.open("drawer");
          } else {
            applyState();
          }
        }
      });
      obs.observe(wrapper, { attributes: true, attributeFilter: ["class"] });
    },

    observeCartMutations: function () {
      var debounced = (function () {
        var t;
        return function () {
          clearTimeout(t);
          t = setTimeout(function () { applyState(); }, 60);
        };
      })();
      var obs = new MutationObserver(function (mutations) {
        for (var i = 0; i < mutations.length; i++) {
          if (mutations[i].addedNodes && mutations[i].addedNodes.length) {
            debounced();
            return;
          }
        }
      });
      var targets = [
        document.getElementById("minicart-form"),
        document.querySelector(".cart-info"),
        document.getElementById("main-cart-items"),
      ];
      targets.forEach(function (node) {
        if (node) obs.observe(node, { childList: true, subtree: true });
      });
    },

    bindCheckout: function () {
      document.addEventListener(
        "submit",
        function (e) {
          var form = e.target;
          if (!form || (form.id !== "cart" && form.id !== "minicart")) return;
          // Store pickup chosen in the ShipZip widget: no delivery gating and
          // no shipping-address prefill — let the app's own checkout flow run.
          if (isPickupMode()) return;
          var result = resolveCurrent();
          // Block checkout entirely when the cart isn't deliverable here.
          if (result && result.blocked) {
            e.preventDefault();
            e.stopImmediatePropagation();
            renderBanner(result);
            return;
          }
          var loc = getStored();
          if (!loc) return;
          e.preventDefault();
          e.stopImmediatePropagation();
          var params = new URLSearchParams();
          params.set("checkout[shipping_address][country]", "Australia");
          params.set("checkout[shipping_address][province]", loc.state);
          params.set("checkout[shipping_address][zip]", loc.zip);
          window.location.href = "/checkout?" + params.toString();
        },
        true
      );
    },

    /**
     * Re-apply the gate when the shopper switches ShipZip tabs. The app
     * toggles the `active` / `d_active` classes itself (and re-renders the
     * widget asynchronously), so watch class changes inside the widget and
     * only re-run when pickup mode actually flipped — avoids ping-ponging
     * with the app's own re-renders.
     */
    observePickupTab: function () {
      var root = document.getElementById("csShippingAppCode");
      if (!root) return;
      var last = isPickupMode();
      var timer;
      var check = function () {
        clearTimeout(timer);
        timer = setTimeout(function () {
          var now = isPickupMode();
          if (now === last) return;
          last = now;
          applyState();
        }, 60);
      };
      new MutationObserver(check).observe(root, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["class"],
      });
      root.addEventListener("click", check, true);
    },

    bindChipEdit: function () {
      var self = this;
      document.addEventListener("click", function (e) {
        var editBtn = e.target.closest("[data-clg-edit]");
        if (!editBtn) return;
        e.preventDefault();
        self.open(isCartPage() ? "cart" : "drawer");
      });
    },
  };

  // Expose for debugging / the checkout gate to re-run after edits.
  window.CartLocationGate = CartLocationGate;
  window.CartLocationGate.applyState = applyState;
  window.CartLocationGate.getStored = getStored;
  window.CartLocationGate.setStored = setStored;
  window.CartLocationGate.clearStored = clearStored;

  function boot() {
    CartLocationGate.init();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();
